import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "@/lib/api";
import { ArrowRight, Clock3, Video, ChevronRight, Sparkles } from "lucide-react";

export default function Blog() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/public/blog`);
        setArticles(data.articles || []);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
    document.title = "Videoepreneur Blog · Playbooks, comparisons, growth tactics";
  }, []);

  const featured = articles[0];
  const rest = articles.slice(1);

  return (
    <div className="min-h-screen bg-[#FBFBFD]">
      {/* Nav */}
      <nav className="max-w-[1280px] mx-auto flex items-center justify-between px-5 sm:px-8 py-6" data-testid="blog-nav">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-[#0B5CFF] flex items-center justify-center">
            <Video className="w-5 h-5 text-white" strokeWidth={1.75} />
          </div>
          <span className="font-display font-semibold text-lg tracking-tight">Videoepreneur</span>
        </Link>
        <div className="flex items-center gap-4 text-sm">
          <Link to="/#pricing" className="text-[#4A4D57] hover:text-[#0B0D17]">Pricing</Link>
          <Link to="/login" className="text-[#4A4D57] hover:text-[#0B0D17]">Log in</Link>
          <Link to="/signup" className="vp-btn-primary text-sm">Free me shuru karo</Link>
        </div>
      </nav>

      {/* Hero */}
      <header className="max-w-[1280px] mx-auto px-5 sm:px-8 pt-8 pb-14">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#E4E6EC] text-xs">
          <Sparkles className="w-3.5 h-3.5 text-[#7C3AED]" />
          <span className="text-[#4A4D57]">Videoepreneur playbook · India creator economy</span>
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.02] tracking-tight max-w-3xl">
          Real playbooks. <span className="text-[#0B5CFF]">Zero fluff.</span>
        </h1>
        <p className="text-[#4A4D57] text-lg mt-4 max-w-2xl leading-relaxed">
          Actual data se guides, comparisons, aur growth tactics — Indian creators, coaches aur teachers ke liye. Hinglish tone, no theory.
        </p>
      </header>

      {loading ? (
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8 pb-24 text-[#4A4D57]">Loading articles…</div>
      ) : (
        <>
          {/* Featured */}
          {featured && (
            <section className="max-w-[1280px] mx-auto px-5 sm:px-8 pb-12">
              <Link
                to={`/blog/${featured.slug}`}
                data-testid={`blog-featured-${featured.slug}`}
                className="group grid lg:grid-cols-12 gap-6 lg:gap-10 items-center vp-card p-6 sm:p-8 hover:shadow-[0_18px_44px_rgba(11,92,255,0.10)] transition-all"
              >
                <div
                  className="lg:col-span-5 aspect-[16/10] rounded-xl relative overflow-hidden flex items-end p-6"
                  style={{ background: featured.hero_gradient }}
                >
                  <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.35), transparent 60%)" }} />
                  <div className="relative text-white">
                    <div className="text-[10px] uppercase tracking-widest font-bold opacity-90">FEATURED · {featured.category}</div>
                    <div className="font-display font-black text-xl mt-1 leading-tight">{featured.title.split(" — ")[0] || featured.title.slice(0, 46)}</div>
                  </div>
                </div>
                <div className="lg:col-span-7">
                  <div className="flex items-center gap-3 flex-wrap text-xs text-[#747680]">
                    <span className="px-2.5 py-1 rounded-full bg-[#0B5CFF]/10 text-[#0B5CFF] font-semibold">{featured.category}</span>
                    <span className="inline-flex items-center gap-1"><Clock3 className="w-3 h-3" /> {featured.read_min} min read</span>
                    <span>· {new Date(featured.published_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
                  </div>
                  <h2 className="font-display font-black text-2xl sm:text-3xl lg:text-4xl mt-3 leading-tight tracking-tight text-[#0B0D17] group-hover:text-[#0B5CFF] transition-colors">
                    {featured.title}
                  </h2>
                  <p className="text-[#4A4D57] mt-3 leading-relaxed">{featured.excerpt}</p>
                  <div className="mt-5 inline-flex items-center gap-1.5 text-[#0B5CFF] font-semibold text-sm">
                    Read the playbook <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            </section>
          )}

          {/* Grid */}
          <section className="max-w-[1280px] mx-auto px-5 sm:px-8 pb-24">
            <div className="vp-label mb-4">More articles</div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="blog-list">
              {rest.map((a) => (
                <Link
                  key={a.slug}
                  to={`/blog/${a.slug}`}
                  data-testid={`blog-card-${a.slug}`}
                  className="group vp-card overflow-hidden hover:shadow-[0_14px_36px_rgba(11,92,255,0.08)] hover:-translate-y-1 transition-all"
                >
                  <div
                    className="aspect-[16/10] relative overflow-hidden flex items-end p-5"
                    style={{ background: a.hero_gradient }}
                  >
                    <div className="absolute inset-0 opacity-30" style={{ background: "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.4), transparent 55%)" }} />
                    <div className="relative text-white">
                      <div className="text-[10px] uppercase tracking-widest font-bold opacity-90">{a.category}</div>
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 text-xs text-[#747680]">
                      <Clock3 className="w-3 h-3" />
                      <span>{a.read_min} min</span>
                      <span>·</span>
                      <span>{new Date(a.published_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</span>
                    </div>
                    <h3 className="font-display font-bold text-lg mt-2 leading-snug text-[#0B0D17] group-hover:text-[#0B5CFF] transition-colors line-clamp-3">
                      {a.title}
                    </h3>
                    <p className="text-sm text-[#4A4D57] mt-2 line-clamp-2 leading-snug">{a.excerpt}</p>
                    <div className="mt-4 inline-flex items-center gap-1 text-[#0B5CFF] font-semibold text-xs">
                      Read <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          {/* Bottom CTA */}
          <section className="bg-[#0B0D17] text-white">
            <div className="max-w-[1280px] mx-auto px-5 sm:px-8 py-16 text-center">
              <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight">
                Yeh sab padha? Ab try karo.
              </h2>
              <p className="text-white/70 mt-3">Free plan. Card nahi chahiye. Pehli sale ke baad hi upgrade.</p>
              <Link to="/signup" className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-full bg-gradient-to-r from-[#F0ABFC] to-[#22D3EE] text-[#0B0D17] font-bold" data-testid="blog-bottom-cta">
                Free me shuru karo <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
